import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import axios from 'axios'
import {
  fetchPDFDataWithCoordinates,
  fetchPDFDataWithOutCoordinates,
  transformDataWithCoordinates,
  transformDataWithOutCoordinates,
  fetchSearchResultsPDF
} from '../../utils/Api'
import {
  Button,
  Tooltip,
  Slider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Chip
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SaveIcon from '@mui/icons-material/Save'
import RefreshIcon from '@mui/icons-material/Refresh'
import CancelIcon from '@mui/icons-material/Cancel'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

// PDF.js worker setup
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

// Add helper function for scrolling
const scrollIntoViewIfNeeded = (container, element) => {
  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  if (
    elementRect.top < containerRect.top ||
    elementRect.bottom > containerRect.bottom
  ) {
    container.scrollBy({
      top:
        elementRect.top -
        containerRect.top -
        container.clientHeight / 2 +
        elementRect.height / 2,
      behavior: 'smooth'
    })
  }
}

const PdfJsonViewer = ({
  closeDialog,
  currentPdfLink,
  docId,
  applicationId,
  setWarningMessage
}) => {
  const [textPositions, setTextPositions] = useState([])
  const [jsonData, setJsonData] = useState([]) // Current JSON data
  const [originalJsonData, setOriginalJsonData] = useState([]) // Original JSON data
  const [numPages, setNumPages] = useState(0) // Total pages count
  const [activePage, setActivePage] = useState(1)
  const [changedProperties, setChangedProperties] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [zoomLevel, setZoomLevel] = useState(1) // Add zoom level state
  const [showOnlyFilled, setShowOnlyFilled] = useState(true)
  const [searchQuery, setSearchQuery] = useState('') // New search field state
  const [searchApiData, setSearchApiData] = useState(null) // State for search API response
  const [searchInput, setSearchInput] = useState('') // State for search input
  const [searchLoading, setSearchLoading] = useState(false) // State for search loading
  const [pdfWidth, setPdfWidth] = useState(0) // State for PDF width
  const [showSearchResults, setShowSearchResults] = useState(true) // Toggle for search results display
  const [highlightKey, setHighlightKey] = useState(null) // New state to hold the key to highlight on navigation

  const jsonEditorRef = useRef(null)
  const pdfViewerRef = useRef(null)

  // Helper to check if a key exists in filtered JSON recursively.
  const hasKeyInFiltered = useCallback((obj, searchKey) => {
    if (!obj || typeof obj !== 'object') return false
    if (Object.hasOwn(obj, searchKey)) return true
    return Object.values(obj).some(
      val => typeof val === 'object' && hasKeyInFiltered(val, searchKey)
    )
  }, [])

  useEffect(() => {
    const fetchDataAsync = async () => {
      setIsLoading(true)
      try {
        const textPosResponse = await fetchPDFDataWithCoordinates({
          pageNumber: activePage || 1,
          doc_id: docId,
          application_id: applicationId
        })
        const jsonResponse = await fetchPDFDataWithOutCoordinates({
          pageNumber: activePage || 1,
          doc_id: docId,
          application_id: applicationId
        })
        const transformedTextPositions =
          transformDataWithCoordinates(textPosResponse)
        setTextPositions(transformedTextPositions)
        if (
          transformedTextPositions.length === 0 ||
          (transformedTextPositions[0] && transformedTextPositions[0].x1 === -1)
        ) {
          setWarningMessage('No marking data found for this PDF.')
        }
        const transformedJsonData =
          transformDataWithOutCoordinates(jsonResponse)[0]
        setJsonData(transformedJsonData)
        setOriginalJsonData(transformedJsonData)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchDataAsync()
  }, [activePage, docId, applicationId, setWarningMessage])

  useEffect(() => {
    const updatePdfWidth = () => {
      if (pdfViewerRef.current) {
        setPdfWidth(pdfViewerRef.current.clientWidth)
      }
    }
    updatePdfWidth()
    window.addEventListener('resize', updatePdfWidth)
    return () => window.removeEventListener('resize', updatePdfWidth)
  }, [])

  // Modified handleJsonClick that now works even when event is not provided
  const handleJsonClick = (key, event = null) => {
    const targetElement = document.getElementById(`json-key-${key}`)
    const overlayElement = document.getElementById(key)

    if (targetElement && jsonEditorRef.current) {
      document.querySelectorAll('.json-key').forEach(jsonKey => {
        jsonKey.style.backgroundColor = 'white'
      })
      targetElement.style.backgroundColor = 'rgba(0, 255, 0, 0.3)'
      scrollIntoViewIfNeeded(jsonEditorRef.current, targetElement)
    }

    if (overlayElement && pdfViewerRef.current) {
      document.querySelectorAll('.overlay').forEach(overlay => {
        overlay.style.backgroundColor = 'rgba(255, 255, 0, 0.3)'
      })
      overlayElement.style.backgroundColor = 'rgba(0, 255, 0, 0.3)'
      scrollIntoViewIfNeeded(pdfViewerRef.current, overlayElement)
    }
  }

  const handleJsonChange = (keyPath, value, prop_id) => {
    const key = keyPath.join('.')
    setJsonData(prevData => {
      const updatedData = { ...prevData }
      let current = updatedData
      for (let i = 0; i < keyPath.length - 1; i++) {
        current = current[keyPath[i]]
      }
      current[keyPath[keyPath.length - 1]] = `${value}$$${prop_id}`
      return updatedData
    })
    setChangedProperties(prev => {
      const updated = [...prev]
      const index = updated.findIndex(
        item => item.key === key && item.id === prop_id
      )
      if (index !== -1) {
        updated[index].value = value
      } else {
        updated.push({ key, value, id: prop_id })
      }
      return updated
    })
  }

  const handleReset = () => {
    setJsonData(originalJsonData)
    setChangedProperties([])
    if (jsonEditorRef.current) {
      jsonEditorRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleResetSearch = () => {
    setSearchApiData(null) // Clear search results
    setSearchInput('') // Clear search input
    setWarningMessage('') // Clear any warning messages
  }

  const handleUpdate = async () => {
    const accessToken = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')
    const headers = {
      'X-Access-Token': accessToken,
      'X-Refresh-Token': refreshToken,
      Accept: 'application/json'
    }
    const promises = changedProperties.map(async property => {
      const { key, value, id } = property
      const apiUrl = `https://cipilot-lexai-api-318242574572.us-central1.run.app/application_mapping/mappings/${id}`
      try {
        const response = await axios.put(apiUrl, { key, value }, { headers })
        console.log(`Data for ${key} saved successfully:`, response.data)
      } catch (error) {
        console.error(`Error saving data for ${key}:`, error)
      }
    })
    await Promise.all(promises)
  }

  const handleSearch = async () => {
    if (searchInput.length < 3) {
      setWarningMessage('Please enter at least 3 characters to search.')
      return
    }
    setSearchLoading(true)
    try {
      const response = await fetchSearchResultsPDF({
        doc_id: docId,
        query_text: searchInput
      })
      const transformedData = response.map(item => ({
        ...item,
        coordinates: { x1: item.x1, y1: item.y1, x2: item.x2, y2: item.y2 }
      }))
      setSearchApiData(transformedData)
      setWarningMessage('')
    } catch (error) {
      console.error('Error fetching search data:', error)
      setWarningMessage('Failed to fetch search results.')
    } finally {
      setSearchLoading(false)
    }
  }

  const handlePageNavigation = (pageNumber, key) => {
    setActivePage(pageNumber)
    setHighlightKey(key)
    setShowSearchResults(false) // Hide search results after navigation
  }

  // Replace the existing auto-highlight useEffect with this updated version:
  useEffect(() => {
    if (highlightKey) {
      const maxAttempts = 5
      let attempts = 0
      const tryHighlight = () => {
        const targetElement = document.getElementById(
          `json-key-${highlightKey}`
        )
        if (targetElement) {
          handleJsonClick(highlightKey)
          setHighlightKey(null)
        } else if (attempts < maxAttempts) {
          attempts++
          setTimeout(tryHighlight, 300)
        }
      }
      tryHighlight()
    }
  }, [activePage, highlightKey])

  const handleZoomChange = (event, newValue) => {
    setZoomLevel(newValue / 100) // Convert slider value to scale factor
  }

  const toggleSearchResults = () => {
    setShowSearchResults(prev => !prev) // Toggle search results visibility
  }

  const renderSearchResults = () => {
    return searchApiData.map((item, index) => (
      <div
        key={index}
        style={{
          padding: '10px',
          borderBottom: '1px solid #ddd',
          cursor: 'pointer',
          backgroundColor: '#f9f9f9'
        }}
        onClick={() => handlePageNavigation(item.page_number, item.key)}
      >
        <strong>{item.key} </strong>:<span> {item.value}</span> <br />
        <strong>Page:</strong>{' '}
        <span style={{ color: '#007bff', textDecoration: 'underline' }}>
          {item.page_number}
        </span>
      </div>
    ))
  }

  // Filter JSON data based on the "showOnlyFilled" flag
  const filterJsonData = useCallback(
    data => {
      if (!showOnlyFilled) return data
      if (typeof data !== 'object' || data === null) return data
      const result = {}
      Object.entries(data).forEach(([key, val]) => {
        if (typeof val === 'string') {
          const trimmed = val.split('$$')[0].trim()
          if (
            trimmed !== '' &&
            trimmed !== 'unmarked' &&
            trimmed !== 'unfilled'
          ) {
            result[key] = val
          }
        } else if (typeof val === 'object') {
          const nested = filterJsonData(val)
          if (Object.keys(nested).length > 0) result[key] = nested
        }
      })
      return result
    },
    [showOnlyFilled]
  )

  const filteredJsonData = useMemo(
    () => filterJsonData(jsonData),
    [jsonData, filterJsonData]
  )

  // Additional filtering by search query (matches key or value)
  const filterBySearch = (data, query) => {
    if (!query) return data
    if (typeof data !== 'object' || data === null) return data
    const result = {}
    Object.entries(data).forEach(([key, val]) => {
      const keyMatches = key.toLowerCase().includes(query.toLowerCase())
      if (typeof val === 'string') {
        const valueStr = val.split('$$')[0]
        const valueMatches = valueStr
          .toLowerCase()
          .includes(query.toLowerCase())
        if (keyMatches || valueMatches) {
          result[key] = val
        }
      } else if (typeof val === 'object') {
        const nested = filterBySearch(val, query)
        if (Object.keys(nested).length > 0 || keyMatches) {
          result[key] = nested
        }
      }
    })
    return result
  }

  const searchFilteredJson = useMemo(() => {
    return filterBySearch(filteredJsonData, searchQuery)
  }, [filteredJsonData, searchQuery])

  // Filter text positions for PDF overlays
  const filteredTextPositions = useMemo(() => {
    if (!showOnlyFilled) return textPositions
    return textPositions.filter(item =>
      Object.keys(item).some(key => {
        const val = item[key]?.value
        return (
          val && val !== 'unmarked' && val !== 'unfilled' && val.trim() !== ''
        )
      })
    )
  }, [textPositions, showOnlyFilled])

  const renderJson = (data, keyPath = []) => {
    return Object.keys(data || {}).map(key => {
      const currentPath = [...keyPath, key]
      let value = data[key]
      let prop_id = ''
      if (typeof value === 'string') {
        const parts = value.split('$$')
        value = parts[0]
        prop_id = parts[1] || ''
      }
      const isEdited = changedProperties.some(
        item => item.key === currentPath.join('.')
      )
      return (
        <div
          id={`json-key-${key}`}
          key={currentPath.join('.')}
          style={{
            paddingLeft: keyPath.length * 20,
            backgroundColor: isEdited ? '#FFF3E0' : 'transparent',
            borderLeft: isEdited ? '3px solid #FF9800' : 'none',
            marginBottom: '5px'
          }}
          className='json-key'
          onClick={e => handleJsonClick(currentPath.join('.'), e)}
        >
          <strong>{key}:</strong>{' '}
          {typeof value === 'object' && value !== null ? (
            <details>
              <summary style={{ cursor: 'pointer' }}>{key}</summary>
              <div style={{ paddingLeft: 20 }}>
                {renderJson(value, currentPath)}
              </div>
            </details>
          ) : (
            <input
              type='text'
              value={value}
              onChange={e =>
                handleJsonChange(currentPath, e.target.value, prop_id)
              }
              style={{
                background: '#f5f5f5',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '5px',
                width: '100%'
              }}
            />
          )}
        </div>
      )
    })
  }

  // Memoize the PDF document with overlays
  const memoizedDocument = useMemo(
    () => (
      <div style={{ position: 'relative' }}>
        <Document
          file={{ url: currentPdfLink }}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          className='pdf-doc-wrapper'
        >
          <Page
            pageNumber={activePage}
            width={pdfWidth * zoomLevel} // Adjust width based on zoom level
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
          {filteredTextPositions.map((item, overlayIndex) =>
            Object.keys(item).map(key => {
              if (key !== 'pageNumber' && item[key].coordinates) {
                if (
                  showOnlyFilled &&
                  !hasKeyInFiltered(filteredJsonData, key)
                ) {
                  return null
                }
                const { x1, y1, x2, y2 } = item[key].coordinates
                return (
                  <div
                    id={key}
                    key={`${overlayIndex}-${key}`}
                    onClick={e => handleJsonClick(key, e)}
                    className='overlay'
                    style={{
                      position: 'absolute',
                      left: x1 * zoomLevel, // Adjust overlay positions based on zoom
                      top: y1 * zoomLevel,
                      width: (x2 - x1) * zoomLevel,
                      height: (y2 - y1) * zoomLevel,
                      backgroundColor: 'rgba(255, 255, 0, 0.3)',
                      cursor: 'pointer'
                    }}
                  />
                )
              }
              return null
            })
          )}
        </Document>
      </div>
    ),
    [
      activePage,
      filteredTextPositions,
      pdfWidth,
      currentPdfLink,
      showOnlyFilled,
      filteredJsonData,
      hasKeyInFiltered,
      zoomLevel // Include zoom level in dependencies
    ]
  )

  return isLoading ? (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
      }}
    >
      <CircularProgress />
    </div>
  ) : (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 0 10px 0'
        }}
      >
        {/* <div style={{ display: 'flex', alignItems: 'center' }}>
          <label style={{ marginRight: '10px', fontWeight: 'bold' }}>
            Zoom PDF:
          </label>
          <Slider
            value={zoomLevel * 100}
            onChange={handleZoomChange}
            min={50}
            max={200}
            step={10}
            sx={{ width: '150px', marginRight: '10px' }}
          />
        </div> */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            label='Search Document'
            variant='outlined'
            size='small'
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            sx={{ marginRight: '10px', width: '420px' }}
          />
          <Button
            variant='contained'
            color='primary'
            onClick={handleSearch}
            disabled={searchLoading}
            sx={{ marginRight: '10px' }}
            startIcon={<AutoAwesomeIcon />}
          >
            {searchLoading ? <CircularProgress size={20} /> : 'Search'}
          </Button>
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleResetSearch}
            disabled={searchLoading}
          >
            Reset
          </Button>
          {searchInput && (
            <Button
              variant='outlined'
              onClick={toggleSearchResults}
              sx={{ marginRight: '10px', marginLeft: '10px' }}
            >
              {showSearchResults
                ? 'Hide Search Results'
                : 'Show Search Results'}
            </Button>
          )}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          overflow: 'hidden',
          height: '72vh',
          padding: '10px',
          background: '#3c3c3c'
        }}
      >
        <div
          ref={pdfViewerRef}
          style={{
            position: 'relative',
            background: '#3c3c3c',
            color: '#fff',
            width: '55%',
            overflow: 'auto',
            resize: 'horizontal'
          }}
        >
          {memoizedDocument}
        </div>
        <div
          className='json-section'
          style={{
            backgroundColor: '#fff',
            border: 'none',
            overflow: 'auto',
            flex: 1,
            padding: '16px'
          }}
          ref={jsonEditorRef}
        >
          {showSearchResults && searchApiData?.length && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <h3>Search Results</h3>
                  <Chip
                    icon={<SmartToyIcon style={{ fontSize: '12px' }} />}
                    label="AI-Powered"
                    size="small"
                    sx={{
                      ml: 1.5,
                      height: '20px',
                      fontSize: '0.7rem',
                      bgcolor: '#ea580c',
                      color: 'white'
                    }}
                  />
                </div>
                {renderSearchResults()}
              </div>
              <hr
                style={{
                  border: 'none',
                  borderTop: '2px dashed #ccc',
                  margin: '20px 0'
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h3>JSON Data</h3>
                <Tooltip title="AI-extracted data">
                  <SmartToyIcon sx={{ ml: 1, color: '#ea580c', fontSize: '1rem' }} />
                </Tooltip>
              </div>
            </>
          )}

          {renderJson(searchFilteredJson)}
        </div>
      </div>
      <div
        style={{
          position: 'sticky',
          bottom: '-20px',
          backgroundColor: '#fff',
          padding: '10px 0 0 10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1000,
          borderTop: '1px solid #ddd'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ChevronLeftIcon
            onClick={() => setActivePage(prev => Math.max(prev - 1, 1))}
            style={{
              fontSize: '2rem',
              color: activePage === 1 ? '#ccc' : '#007bff',
              cursor: activePage === 1 ? 'not-allowed' : 'pointer',
              marginRight: '10px'
            }}
          />
          <span
            style={{
              margin: '0 10px',
              color: 'black',
              fontWeight: 'bold',
              fontSize: '.8rem'
            }}
          >
            Page {activePage} of {numPages}
          </span>
          <ChevronRightIcon
            onClick={() => setActivePage(prev => Math.min(prev + 1, numPages))}
            style={{
              fontSize: '2rem',
              color: activePage === numPages ? '#ccc' : '#007bff',
              cursor: activePage === numPages ? 'not-allowed' : 'pointer'
            }}
          />
          <div
            style={{
              marginLeft: '20px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <label style={{ marginRight: '10px', fontWeight: 'bold' }}>
              Go to page:&nbsp;
              <input
                type='number'
                min='2'
                max={numPages}
                defaultValue={activePage}
                onBlur={e => setActivePage(Number(e.target.value))}
                style={{ width: '50px', padding: '2px' }}
              />
            </label>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyFilled}
                  onChange={e => setShowOnlyFilled(e.target.checked)}
                />
              }
              label='Show only filled'
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Tooltip title='Save changes'>
            <Button
              variant='contained'
              color='success'
              startIcon={<SaveIcon />}
              onClick={handleUpdate}
            >
              Save
            </Button>
          </Tooltip>
          <Tooltip title='Reset to original data'>
            <Button
              variant='contained'
              color='warning'
              startIcon={<RefreshIcon />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Tooltip>
          <Tooltip title='Cancel and close'>
            <Button
              variant='contained'
              color='error'
              startIcon={<CancelIcon />}
              onClick={closeDialog}
            >
              Cancel
            </Button>
          </Tooltip>
        </div>
      </div>
    </>
  )
}

export default PdfJsonViewer
